import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AthletesApp } from './SharedComponents';
import AthleteTable from "./AthleteTable";
import { Athlete, AthleteResult, Gender, OwnedAthlete, Race, Selection, Weekend } from "./models";
import { useLoaderData } from "react-router-dom";
import { ApplicationComponents, Components } from "./Context";
import { Table } from "./ReactComponents";
import { ApplicationLink } from "./Routing";
import {
    formatAmount,
    formatRank,
    formatScore,
    genderToCopy,
    HTTP_CLIENT,
    HTTP_CLIENT_JQUERY_ADAPTER,
    isFirstTeam
} from "./Util";
import { useOwnedAthletes, useRedirectToLoginIfNeeded, useRemoveAthlete, useSetOwnedAthletes } from "./Hooks";
import classNames from "classnames";
import { CopyComponent, TooltipComponent } from "./TypescriptComponents";
import { Filter } from "./AthleteAppComponents";
import { ColumnDef, Getter } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CaptainBandIcon } from "./Images";
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverlay,
    PointerSensor,
    UniqueIdentifier,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';


const DEFAULT_FILTER: Filter = {
    name: '',
    filterPrice: false
}
export const AthleteView = () => {

    const athlete = useLoaderData() as Athlete;

    return <div>
        <h1 className="entry-title">{ athlete.name }</h1>
        <div className="row">
            <div className="medium-6 columns">
                <AthleteInformationTable athlete={ athlete }/>
            </div>
            <div className="medium-12 columns">
                <ResultTable athleteId={ athlete.athlete_id }/>
            </div>
        </div>
    </div>
}

const AthleteInformationTable = ({ athlete }: { athlete?: Athlete }) => {

    const { flag } = useContext<Components>(ApplicationComponents);

    const data: { title: string | React.JSX.Element, value: any, hasValue?: boolean, settings?: any }[] = [
        {
            title: 'Nation:',
            value: athlete ? <React.Fragment>{ athlete.country } { flag(athlete.country) } </React.Fragment> : ""
        },
        { title: 'Score:', value: athlete?.score || '-' },
        { title: 'Rank:', value: athlete?.rank || 'N/A' },
        { title: 'Gender:', value: athlete?.gender === 'm' ? 'Male' : 'Female' },
        { title: 'Price:', value: athlete?.price },
    ];

    const columns = [
        {
            dataFunction: (data: any) => {
                return {
                    content: data.title,
                    settings: data.settings
                }
            },
        },
        {
            dataFunction: (data: any) => {
                return {
                    isLoading: data.value === undefined,
                    content: data.value,
                }
            },
        }
    ];
    return <React.Fragment>
        <h2>Athlete information</h2>
        <Table columns={ columns } data={ data } config={ { hasHeaders: false } }/>
    </React.Fragment>
}

export const ResultTable = ({ athleteId }: { athleteId: string | number }) => {
    const [results, setResults] = useState<AthleteResult[] | undefined>();
    const [races, setRaces] = useState<Map<number, Race>>(new Map<number, Race>());
    const [weekends, setWeekends] = useState<Map<number, Weekend>>(new Map<number, Weekend>());

    useEffect(() => {

        HTTP_CLIENT_JQUERY_ADAPTER.get({
            url: `/api/athletes/${ athleteId }/results`,
        }).then((results: AthleteResult[]) => {
            setResults(results);
        });
        HTTP_CLIENT_JQUERY_ADAPTER.get({
            url: '/api/races'
        }).then((races: Race[]) => {
            setRaces(races.reduce((raceMap, race) => {
                raceMap.set(race.race_id, race);
                return raceMap;
            }, new Map<number, Race>()));
        });
        HTTP_CLIENT_JQUERY_ADAPTER.get({
            url: '/api/weekends'
        }).then((weekends: Weekend[]) => {
            setWeekends(weekends.reduce((weekendMap, weekend) => {
                weekendMap.set(weekend.weekend_id, weekend);
                return weekendMap;
            }, new Map<number, Weekend>()));
        });
    }, [athleteId]);

    const columns = [
        {
            dataFunction: (data: AthleteResult) => {
                return {
                    isLoading: !races.has(data.race_id),
                    content: races.get(data.race_id)?.date as string,
                }
            },
            header: 'Date'
        },
        {
            dataFunction: (data: AthleteResult) => {
                return {
                    isLoading: !races.has(data.race_id) || !weekends.has(races.get(data.race_id)?.weekend_id as number),
                    content: <ApplicationLink href={ `/races/${ data.race_id }` }>
                        { weekends.get(races.get(data.race_id)?.weekend_id as number)?.weekend_name } { races.get(data.race_id)?.name }
                    </ApplicationLink>,
                }
            },
            header: 'Race'
        },
        {
            dataFunction: (data: AthleteResult) => {
                return {
                    content: formatRank(data.rank),
                }
            },
            header: 'Rank'
        },
        {
            dataFunction: (athlete: AthleteResult) => {
                return {
                    content: formatScore(athlete.score),
                }
            },
            header: 'Score'
        }
    ];
    return <Table columns={ columns } data={ results } config={ { loadingRows: 10 } }/>
}

const defaultColumns: ColumnDef<Athlete>[] = [
    {
        header: 'Name',
        sortingFn: (a, b) => a.original.name.localeCompare(b.original.name),
        accessorFn: (athlete) => athlete,
        cell: ({ getValue }: { getValue: Getter<Athlete> }) => <ApplicationLink
            href={ `/athletes/${ getValue().athlete_id }` }>{ getValue().name }</ApplicationLink>,
    },
    {
        header: 'Price',
        accessorFn: (obj: Athlete) => formatAmount(obj.price),
        sortingFn: (a, b) => a.original.price - b.original.price,
        sortDescFirst: true,
    },
    {
        header: 'Score',
        accessorFn: (obj: Athlete) => formatScore(obj.score || 0),
        sortingFn: (a, b) => (a.original.score || 0) - (b.original.score || 0),
        sortDescFirst: true,
    },
    {
        header: 'Nat.',
        accessorKey: 'country',
    },
    {
        header: '',
        accessorKey: 'trade',
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<any> }) => getValue()
    },
];

const DefaultAthletesApp = () => {
    return <AthleteApp columns={ defaultColumns }/>;
}

export const AthleteApp = ({ columns, extraData, allowBuyingFunction }: {
    columns: ColumnDef<any, any>[],
    extraData?: Map<number, any>,
    allowBuyingFunction?: (athlete: Athlete, balance: number, ownedAthletes: OwnedAthlete[], activeSelection: Selection) => boolean
}) => {
    useRedirectToLoginIfNeeded();
    allowBuyingFunction = allowBuyingFunction || allowBuying;

    function getAthletePicker(balance: number, afterDeadline: boolean, athletes: Athlete[], activeSelection: Selection, weekend: Weekend) {

        const selectionColumns = activeSelection.budgetEnforced ? columns : columns.filter(col => col.header !== 'Price');
        let availableAthletes = athletes;

        if (activeSelection.takesTeamPicks) {
            availableAthletes = availableAthletes.filter(athlete => athlete.is_team);
            if (weekend.is_championship) {
                availableAthletes = availableAthletes
                    .filter(athlete => isFirstTeam(athlete))
                    .map(athlete => {
                        const lastWordIndex = athlete.name.lastIndexOf(" ");

                        return {
                            ...athlete,
                            name: athlete.name.substring(0, lastWordIndex)
                        }
                    })
            }
        } else {
            availableAthletes = availableAthletes.filter(athlete => !athlete.is_team);
        }

        if (activeSelection.isMixed) {
            availableAthletes = availableAthletes.filter(athlete => athlete.gender === 'mixed');
        } else {
            availableAthletes = availableAthletes.filter(athlete => athlete.gender !== 'mixed');
        }

        if (activeSelection.isCoed && !activeSelection.takesTeamPicks) {
            return <CoedGenderedAthletePicker balance={ balance }
                                              activeSelection={ activeSelection }
                                              afterDeadline={ afterDeadline }
                                              athletes={ availableAthletes }
                                              columns={ selectionColumns }
                                              extraData={ extraData || new Map() }
                                              allowBuyingFunction={ allowBuyingFunction! }
            />;
        } else if (activeSelection.isCoed && activeSelection.takesTeamPicks) {
            return <CoedTeamAthletePicker balance={ balance }
                                          activeSelection={ activeSelection }
                                          afterDeadline={ afterDeadline }
                                          athletes={ availableAthletes }
                                          columns={ selectionColumns }
                                          extraData={ extraData || new Map() }
                                          allowBuyingFunction={ allowBuyingFunction! }
            />;
        }

        return <AthletePicker balance={ balance }
                              activeSelection={ activeSelection }
                              afterDeadline={ afterDeadline }
                              athletes={ availableAthletes }
                              columns={ selectionColumns }
                              extraData={ extraData || new Map() }
                              allowBuyingFunction={ allowBuyingFunction! }
        />;
    }

    return (
        <AthletesApp>
            {
                ({
                     balance,
                     afterDeadline,
                     athletes,
                     activeSelection,
                     weekend
                 }: AthleteAppProps) => (
                    <div className="App">
                        <h2>My team</h2>
                        <Balance balance={ balance } selection={ activeSelection }/>
                        { getAthletePicker(balance, afterDeadline, athletes, activeSelection, weekend) }
                    </div>
                )
            }
        </AthletesApp>
    )
}

const Balance = ({ balance, selection }: { balance: number, selection: Selection }) => {
    if (!selection.budgetEnforced) {
        return <></>;
    }
    return <h3 className="balance">Balance: { formatAmount(balance) }</h3>;
}

const allowBuying = (athlete: Athlete, balance: number, ownedAthletes: OwnedAthlete[], activeSelection: Selection) => {
    if (!activeSelection) {
        return false;
    }
    if (!activeSelection.isCoed) {
        ownedAthletes = ownedAthletes.filter((ownedAthlete) => ownedAthlete.athlete.gender === athlete.gender);
    }

    if (activeSelection.budgetEnforced && athlete.price > balance) {
        return false;
    }
    return ownedAthletes.length < activeSelection.maxPicksPerGender;
}

function loadFilterFromSearchParams(prefix: string): Filter {
    const params = (new URL(document.URL)).searchParams;
    const filteredValues = Array.from(params.entries())
        .filter((value) => value[0].startsWith(prefix))
        .reduce((collector, value) => {
            collector.set(value[0].substring(prefix.length), value[1])
            return collector;
        }, new Map<string, string>());
    return {
        filterPrice: filteredValues.get('FilterPrice')?.toLowerCase() === 'true',
        name: filteredValues.get('Name') || DEFAULT_FILTER.name
    };
}

function saveFilterToSearchParams(prefix: string, filter: Filter) {
    const params = (new URL(document.URL)).searchParams;
    const filteredValues = Array.from(params.entries()).filter((value) => !value[0].startsWith(prefix));
    if (filter.filterPrice !== DEFAULT_FILTER.filterPrice && filter.filterPrice !== undefined) {
        filteredValues.push([prefix + "FilterPrice", filter.filterPrice.toString()]);
    }
    if (filter.name !== DEFAULT_FILTER.name && filter.name !== undefined) {
        filteredValues.push([prefix + "Name", filter.name]);
    }
    let query = filteredValues.reduce((collector, value) => `${ collector }${ value[0] }=${ value[1] }&`, "");
    query = query.substring(0, query.length - 1);
    if (query.length) {
        window.history.replaceState({}, '', `/athletes?${ query }`)
    } else {
        window.history.replaceState({}, '', `/athletes`)
    }
}

const wildcardFilter = () => true;

const OwnedAthletesHeader = (props: {gender: Gender, selection: Selection}) => {
    let ownedAthletes = useOwnedAthletes(props.selection.id).data as OwnedAthlete[];
    let count = "";
    if (props.selection.takesRankedPicks) {
        if (!props.selection.isMixed) {
            ownedAthletes = ownedAthletes.filter(ath => ath.athlete.gender === props.gender);
        }
        count = ownedAthletes.length + "/" + props.selection.maxPicksPerGender;
    }
    return <h3>My {genderToCopy(props.gender)} {count}</h3>
}

const genderFilter = (gender: Gender) => (athlete: Athlete) => athlete.gender === gender;

const GenderedAthletePicker = ({
                                   balance,
                                   afterDeadline,
                                   athletes,
                                   activeSelection,
                                   columns,
                                   extraData,
                                   allowBuyingFunction,
                                   gender
                               }:
                                   AthletePickerProps & {gender: Gender}) => {
    const [filter, setFilter] = useState<Filter>(loadFilterFromSearchParams(gender));
    function updateFilter(filter: Filter) {
        setFilter(filter);
        saveFilterToSearchParams(gender, filter)
    }

    return <>
        <OwnedAthletesHeader gender={gender} selection={activeSelection} />
        <OwnedAthletes rows={ activeSelection.maxAthletePicks() / 2 }
                       columns={ 2 }
                       afterDeadline={ afterDeadline }
                       selection={ activeSelection }
                       gender={ gender }/>
        <TeamCaptainExplanation selection={ activeSelection }/>
        <AthleteTable athletes={ activeSelection.isMixed ? athletes : athletes.filter(genderFilter(gender)) }
                      allowBuying={ allowBuyingFunction }
                      afterDeadline={ afterDeadline }
                      columns={ columns }
                      balance={ balance }
                      activeSelection={ activeSelection }
                      filter={ filter }
                      filterUpdate={ updateFilter }
                      extraData={ extraData }
        />
    </>
}

const AthletePicker = (props:
                           AthletePickerProps) => {
    const explanationComponent = props.activeSelection.takesRankedPicks ? <div className="medium-12 columns"><CopyComponent copyKey={"rank-explanation"} /></div> : <></>;
    return <div className="row">
        {explanationComponent}
        <div className="medium-6 columns">
            <GenderedAthletePicker {...props} gender={'m'} />
        </div>
        <div className="medium-6 columns">
            <GenderedAthletePicker {...props} gender={'f'} />
        </div>
    </div>
}

type OwnedAthletesProps = {
    afterDeadline: boolean,
    rows: number,
    columns: number,
    selection: Selection,
    gender: Gender | undefined
};

const OwnedAthletes = (props: OwnedAthletesProps) => {
    if (props.selection.takesRankedPicks) {
        return <RankedOwnedAthletes {...props} />;
    }
    return <NotRankedOwnedAthletes {...props} />;
}

const NotRankedOwnedAthletes = (props: OwnedAthletesProps) => {

    const { athleteIndicators } = useContext<Components>(ApplicationComponents)
    const ownedAthletes = useOwnedAthletes(props.selection.id).data as OwnedAthlete[];
    const filteredOwnedAthletes = ownedAthletes.filter(athlete => props.selection.isCoed || !props.gender || genderFilter(props.gender)(athlete.athlete));
    const { mutate: removeAthlete } = useRemoveAthlete(props.selection.id);

    function fetchAthlete(index: number) {
        if (index < filteredOwnedAthletes.length) {
            const user_athlete = filteredOwnedAthletes[index];

            let name = user_athlete.athlete.name;

            if (name.length > 21) {
                name = name.slice(0, 18) + '...';
            }

            return (
                <div>
                    { props.selection.teamCaptainsEnabled
                        && <CaptainBand athlete={ user_athlete } selection={ props.selection }/>
                    }
                    <ApplicationLink
                        href={ `/athletes/${ user_athlete.athlete.athlete_id }` }>{ name }</ApplicationLink>&nbsp;
                    <span>{ formatAmount(user_athlete.bought_for) }</span>
                    { athleteIndicators(user_athlete.athlete) }
                    { !props.afterDeadline &&
                        <button className="button float-right tiny"
                                onClick={ () => {
                                    removeAthlete(user_athlete.athlete)
                                } }>&#10006;</button>
                    }
                </div>
            );
        } else {
            return <div>&nbsp;</div>;
        }
    }

    function createTableBody() {
        const selected = [];

        for (let row = 0; row < props.rows; row++) {
            const children = [];

            for (let column = 0; column < props.columns; column++) {
                children.push(
                    <td key={ "cell" + row + column } className={ "selected_athlete large-" + (12 / props.columns) }>
                        { fetchAthlete((row * props.columns) + column) }
                    </td>
                );
            }

            selected.push(<tr key={ "row" + row }>{ children }</tr>);
        }

        return selected;
    }

    return (
        <table>
            <tbody>
            { createTableBody() }
            </tbody>
        </table>
    )
}

const CaptainBand = ({ athlete, selection }: {
    athlete: OwnedAthlete,
    selection: Selection
}) => {

    const tooltipCopy = athlete.is_team_captain ? "Selected team captain" : "Select team captain";
    const queryClient = useQueryClient();
    const { mutate: makeTeamCaptain } = useMutation({
        mutationFn: () => {
            return HTTP_CLIENT.put(`/api/me/teams/selections/${ selection.id }/${ athlete.athlete_id }/make_team_captain`, true)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ownedAthletes', selection.id] })
        }
    })

    return <>
        <span className={ classNames("captain-band", { "clickable": selection.tradingOpen }) }>
            <span onClick={ () => makeTeamCaptain() }
                  className={ classNames("captain", { "notSelected": !athlete.is_team_captain }) }><CaptainBandIcon/></span>

            { (athlete.is_team_captain || selection.tradingOpen) &&
                <TooltipComponent text={ tooltipCopy } onClick={ () => makeTeamCaptain() }/> }
        </span>
    </>

}

const CoedTeamAthletePicker = (props: AthletePickerProps) => {
    const explanationComponent = props.activeSelection.takesRankedPicks ? <div className="medium-12 columns"><CopyComponent copyKey={"rank-explanation"} /></div> : <></>;

    return <div className="row">
        {explanationComponent}
        <div className="medium-12 columns">
            <GenderedAthletePicker {...props} gender={'mixed'} />
        </div>
    </div>;
}

const CoedGenderedAthletePicker = ({
                                       balance,
                                       afterDeadline,
                                       athletes,
                                       activeSelection,
                                       columns,
                                       extraData,
                                       allowBuyingFunction
                                   }: AthletePickerProps) => {

    const [maleFilter, setMaleFilter] = useState<Filter>(loadFilterFromSearchParams("men"));
    const [womenFilter, setWomanFilter] = useState<Filter>(loadFilterFromSearchParams("women"));

    function updateMaleFilter(filter: Filter) {
        setMaleFilter(filter);
        saveFilterToSearchParams("men", filter)
    }

    function updateWomenFilter(filter: Filter) {
        setWomanFilter(filter);
        saveFilterToSearchParams("women", filter)
    }

    return <div className="row">
        <div className="medium-12 columns">
            <h3>My Athletes</h3>
            <OwnedAthletes rows={ activeSelection.maxAthletePicks() / 2 }
                           columns={ 2 }
                           afterDeadline={ afterDeadline }
                           selection={ activeSelection }
                           gender={ undefined }/>
            <TeamCaptainExplanation selection={ activeSelection }/>
        </div>

        <div className="medium-6 columns">
            <AthleteTable athletes={ athletes.filter(athlete => athlete.gender === 'm') }
                          allowBuying={ allowBuyingFunction }
                          afterDeadline={ afterDeadline }
                          columns={ columns }
                          balance={ balance }
                          activeSelection={ activeSelection }
                          filter={ maleFilter }
                          filterUpdate={ updateMaleFilter }
                          extraData={ extraData }
            />
        </div>
        <div className="medium-6 columns">
            <AthleteTable athletes={ athletes.filter(athlete => athlete.gender === 'f') }
                          allowBuying={ allowBuyingFunction }
                          afterDeadline={ afterDeadline }
                          columns={ columns }
                          balance={ balance }
                          activeSelection={ activeSelection }
                          filter={ womenFilter }
                          filterUpdate={ updateWomenFilter }
                          extraData={ extraData }
            />
        </div>
    </div>;
}

const TeamCaptainExplanation = ({ selection }: { selection: Selection }) => {
    return selection.teamCaptainsEnabled ? <p>Team captains will get 50% extra champion points</p> : <></>;
}

interface AthleteAppProps {
    balance: number,
    afterDeadline: boolean,
    athletes: Athlete[],
    activeSelection: Selection,
    columns: ColumnDef<any, any>[],
    extraData: Map<number, any>,
    allowBuyingFunction: (athlete: Athlete, balance: number, ownedAthletes: OwnedAthlete[], activeSelection: Selection) => boolean,
    weekend: Weekend
}

interface AthletePickerProps {
    balance: number,
    afterDeadline: boolean,
    athletes: Athlete[],
    activeSelection: Selection,
    columns: ColumnDef<any, any>[],
    extraData: Map<number, any>,
    allowBuyingFunction: (athlete: Athlete, balance: number, ownedAthletes: OwnedAthlete[], activeSelection: Selection) => boolean,
}

export const SelectionSelector = ({ selections, handleClone, handleSwitchActiveSelection, activeSelection }:
                                      {
                                          selections: Selection[],
                                          handleClone: any,
                                          handleSwitchActiveSelection: any,
                                          activeSelection: Selection
                                      }) => {

    const [hasWarning, setHasWarning] = useState<number[]>([])

    const selectionComponents = selections
        .sort((a, b) => new Date(a.tradingClose).getTime() - new Date(b.tradingClose).getTime())
        .map(selection => {
            return <SelectionComponent selection={ selection }
                                       handleClone={ handleClone }
                                       handleSwitchActiveSelection={ handleSwitchActiveSelection }
                                       key={ selection.id }
                                       activeSelection={ activeSelection }
                                       setHasWarning={ (warn: boolean) => {
                                           if (warn) {
                                               const copy = [...hasWarning];
                                               copy.push(selection.id);
                                               setHasWarning(copy);
                                           } else {
                                               const copy = hasWarning.filter(id => id !== selection.id);
                                               setHasWarning(copy);
                                           }
                                       } }/>
        });

    return <div className="row">
        <div className="medium-12 columns">
            <table className="table">
                <thead>
                <tr>
                    <th>Race(s)</th>
                    <th>Deadline</th>
                    <th/>
                    <th/>
                </tr>
                </thead>
                <tbody>
                { selectionComponents }
                </tbody>
            </table>
        </div>
        { (hasWarning.length &&
            <div className="medium-12 columns">
                <sup>1</sup> You have not selected a team for this selection
            </div>) || <></>
        }
    </div>
};

function getBadgeClass(selection: Selection, team: OwnedAthlete[]): 'alert' | 'warning' | 'hide' {
    const tradingClose = new Date(selection.tradingClose);
    const oneDay = 1000 * 60 * 60 * 24;
    if (tradingClose.getTime() - (new Date().getTime() + oneDay) <= 0 && !team.length) {
        return 'alert';
    } else if (tradingClose.getTime() - (new Date().getTime() + oneDay * 3) <= 0 && !team.length) {
        return 'warning';
    }
    return 'hide';
}

const SelectionComponent = (props: {
    selection: Selection,
    activeSelection: Selection,
    handleClone: any,
    handleSwitchActiveSelection: any,
    setHasWarning: (warn: boolean) => void;
}) => {

    let team = useOwnedAthletes(props.selection.id).data as OwnedAthlete[];
    const badgeClass = getBadgeClass(props.selection, team);
    const { selection, activeSelection, handleClone, handleSwitchActiveSelection } = props;
    const isActive = selection === activeSelection;
    const compatiblePicks = selection.takesTeamPicks === activeSelection.takesTeamPicks;
    let className = '';

    if (isActive) {
        className = 'activeSelection';
    } else if (new Date(selection.tradingClose) < new Date()) {
        className = 'primary';
    }

    const tradingClose = new Date(selection.tradingClose);

    useEffect(() => {
        props.setHasWarning(badgeClass !== 'hide');
    }, [badgeClass]);

    return <tr className={ className }>
        <td>{ selection.name }</td>
        <td>
                <span style={ { 'float': 'left' } }>
                    { tradingClose.toLocaleString(undefined, { timeZoneName: 'short' }) }
                </span>
            { selection && <span className={ classNames("badge", badgeClass) }
                                 style={ { 'float': 'left' } }>1</span> }
        </td>
        <td>{ !isActive && activeSelection.tradingOpen &&
            <button className="button"
                    onClick={ (event) => handleClone(event, selection.id) }
                    disabled={ !compatiblePicks }>
                Copy this team
            </button>
        }</td>
        <td>{ !isActive &&
            <button className="button" onClick={ (event) => handleSwitchActiveSelection(event, selection) }>
                { selection.tradingOpen ? 'Pick' : 'View' } team <i className="fi-fast-forward"/>
            </button>
        }</td>
    </tr>
}

const RankedOwnedAthletes = (props: OwnedAthletesProps) => {
    const [activeId, setActiveId] = useState<UniqueIdentifier | undefined>();
    const cachedOwnedAthletes = useOwnedAthletes(props.selection.id).data as OwnedAthlete[];
    const { mutate: updateOwnedAthletes, variables, isPending } = useSetOwnedAthletes(props.selection.id);
    const [ownedAthletes, setOwnedAthletesState] = useState<OwnedAthlete[]>([]);
    const sensors = useSensors(
        useSensor(PointerSensor)
    );

    useEffect(() => {
        if (!isPending) {
            setOwnedAthletesState(cachedOwnedAthletes);
        }
    }, [cachedOwnedAthletes, isPending]);

    function setOwnedAthletes(ownedAthletes: OwnedAthlete[]) {
        setOwnedAthletesState(ownedAthletes);
        updateOwnedAthletes(ownedAthletes);
    }

    function removeAthlete(athlete: Athlete) {
        const otherAthletes = ownedAthletes.filter(ath => !sortedOwnedAthletes.find(otherAth => otherAth.athlete_id === ath.athlete_id));
        const newOwnedAthletes = sortedOwnedAthletes
            .filter(ath => ath.athlete_id !== athlete.athlete_id)
            .map((ath, index) => {
                return {
                    ...ath,
                    id: undefined,
                    rank: index + 1
                }
        });
        setOwnedAthletes(otherAthletes.concat(newOwnedAthletes));
    }

    const sortedOwnedAthletes = useMemo(() => {
        return (isPending ? variables : ownedAthletes).filter(oa => props.selection.isCoed || !props.gender || genderFilter(props.gender)(oa.athlete))
            .sort((a1, a2) => a1.rank! - a2.rank!)
            .map((athlete, index) => {
                return {
                    ...athlete,
                    id: athlete.athlete_id,
                    rank: athlete.rank ?? (index + 1)
                }
            })
    }, [ownedAthletes, isPending, variables]);

    async function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = sortedOwnedAthletes.findIndex((athlete) => athlete.athlete_id === active.id);
            const newIndex = sortedOwnedAthletes.findIndex((athlete) => athlete.athlete_id === over.id);
            const newOwnedAthletes = arrayMove(sortedOwnedAthletes, oldIndex, newIndex);
            const otherAthletes = ownedAthletes.filter(ath => !sortedOwnedAthletes.find(otherAth => otherAth.athlete_id === ath.athlete_id));
            let mergedAthletes = newOwnedAthletes.map((ath, index) => {
                return {
                    ...ath,
                    id: undefined,
                    rank: index + 1
                } as OwnedAthlete
            }).concat(otherAthletes);
            await setOwnedAthletes(mergedAthletes);
        }
    }

    const selectedAthlete = sortedOwnedAthletes.find(ath => ath.athlete_id === activeId);
    if (!sortedOwnedAthletes.length) {
        return <div className={"m"} style={{justifyContent: "center", display: "flex", marginBottom: "3em", marginTop: "3em", color: "gray"}}>
            You have not selected any {genderToCopy(props.gender)}
        </div>
    }
    return (
        <div>
            <DndContext
                sensors={ sensors }
                collisionDetection={ closestCenter }
                onDragStart={ event => setActiveId(event.active.id) }
                onDragEnd={ handleDragEnd }
            >
                <div className={ 'sort-container' }>
                    <SortableContext
                        items={ sortedOwnedAthletes }
                        strategy={ verticalListSortingStrategy }
                    >
                        {
                            sortedOwnedAthletes.map((userAthlete, index) =>
                                <SortableItem key={ userAthlete.id }
                                              id={ userAthlete.id }
                                              userAthlete={ userAthlete }
                                              afterDeadline={ props.afterDeadline }
                                              removeAthlete={ () => removeAthlete(userAthlete.athlete) }
                                              isMovable={ sortedOwnedAthletes.length > 1 }
                                              isSellable={ !isPending }/>
                            )
                        }
                    </SortableContext>
                </div>
                <DragOverlay>
                    { selectedAthlete ? (
                        <div
                            className={ classNames(
                                "sort-row",
                                { "blue-background": sortedOwnedAthletes.findIndex((ath) => ath.athlete_id === selectedAthlete.athlete_id) % 2 == 1 },
                                { "white-background": sortedOwnedAthletes.findIndex((ath) => ath.athlete_id === selectedAthlete.athlete_id) % 2 == 0 }
                            ) }>
                            <AthleteCell userAthlete={ selectedAthlete }
                                         afterDeadline={ props.afterDeadline }
                                         removeAthlete={ () => {} }
                                         sellable={ !isPending } />
                            <i className="fi-list"></i>
                        </div>
                    ) : null }
                </DragOverlay>
            </DndContext>
        </div>
    )
}

export function SortableItem(props: {
    id: number,
    userAthlete: OwnedAthlete,
    afterDeadline: boolean,
    removeAthlete: () => void,
    isMovable: boolean,
    isSellable: boolean
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: props.id, disabled: !props.isSellable });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    return (
        <div ref={ setNodeRef } style={ style } className={"sort-row"}>
            <AthleteCell userAthlete={ props.userAthlete }
                         afterDeadline={ props.afterDeadline }
                         removeAthlete={ props.removeAthlete }
                         sellable={ props.isSellable } />
            {props.isMovable ? <span className="fi-list drag-handle"  { ...attributes } { ...listeners }><TooltipComponent text={"Move athlete"} /></span> : <></>}
        </div>
    );
}

const AthleteCell = (props: { userAthlete: OwnedAthlete, afterDeadline: boolean, removeAthlete: () => void, sellable: boolean }) => {
    return <div className={"athlete-cell"}>
        <div>
            <span>{ props.userAthlete.rank }.&nbsp;</span>
            <ApplicationLink
                href={ `/athletes/${ props.userAthlete.athlete.athlete_id }` }>{ props.userAthlete.athlete.name }</ApplicationLink>&nbsp;
        </div>
        {
            !props.afterDeadline && <div>
                <button className="button tiny"
                        style={{"margin": 0}}
                        disabled={ !props.sellable }
                        onClick={ props.removeAthlete }>&#10006;</button>
            </div>
        }
    </div>;
}

export default DefaultAthletesApp;
